import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { SpecialtyViewModel } from '../../../models/Entities/Specialties/SpecialtyModels';
import { ErrorType } from '../../../models/HttpError';
import { AppState } from '../../../redux/reducers';
import { Label, MessageBarBody, Spinner, Switch, Text } from '@fluentui/react-components';
import { GuideDrawer } from '../../../theme/components/GuideDrawer';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';

// Store
import * as SpecialtiesStore from '../../../redux/middleware/Specialties.Middleware';

// styles
import './SpecialtyMultiSelectorComponent.css';

export type SelectedSpecialtyType = {
  specialtyId: number;
  ReferenceId: number;
};

export interface IProps {
  isOpen: boolean;
  openPanel: () => void;
  dismissPanel: () => void;
  SelectedSpecialties: SelectedSpecialtyType[];
  ErrorInformed: ErrorType | undefined;
  onSelect?: (specialty: SpecialtyViewModel) => void;
  onUnselect?: (specialty: SelectedSpecialtyType) => void;
}

interface ConnectedProps {
  isLoading: boolean;
  loadingSuccess: boolean;
  failOnLoading: boolean;
  error: ErrorType;
  Specialties: SpecialtyViewModel[];
}

interface DispatchProps {
  GetAllSpecialties: typeof SpecialtiesStore.actionCreators.GetAllSpecialties;
}

export type Props = IProps & ConnectedProps & DispatchProps;

export const SpecialtyMultiSelectorComponent = ({
  Specialties,
  isLoading,
  failOnLoading,
  ErrorInformed,
  error,
  SelectedSpecialties,
  isOpen,
  dismissPanel,
  onSelect,
  onUnselect,
  GetAllSpecialties
}: Props) => {
  const [t] = useTranslation();

  useEffect(() => {
    if (GetAllSpecialties) GetAllSpecialties();
  }, [GetAllSpecialties]);

  const HandleCancelForm = () => {
    dismissPanel();
  };

  const OnSelectSpecialty = (specialty: SpecialtyViewModel) => {
    if (onSelect) onSelect(specialty);
  };

  const OnUnselectSpecialty = (specialty: SelectedSpecialtyType | undefined) => {
    if (onUnselect && specialty) onUnselect(specialty);
  };

  const optionList = (
    <div className="SpecialtyMultiSelectorContent">
      <GuideMessageBar intent="warning">
        <MessageBarBody>
          {t('specialtyMultiSelector.InformationBaseMessage')}
          &nbsp;{SelectedSpecialties.length}&nbsp;
          {t('specialtyMultiSelector.selectedCount')}
        </MessageBarBody>
      </GuideMessageBar>
      {ErrorInformed && (
        <GuideMessageBar intent="error">
          <MessageBarBody>{ErrorInformed.ErrorMessage}</MessageBarBody>
        </GuideMessageBar>
      )}
      <div className="SelectionSPListContainer">
        <div className="SelectionListRows">
          <div className="SelectionListRow SelectionListRow--selectAll">
            <div className="SelectionList">
              <Switch
                checked={
                  Specialties?.length > 0 &&
                  Specialties.every((s) =>
                    SelectedSpecialties.some((sel) => sel.specialtyId === s.id)
                  )
                }
                onChange={(_, data) => {
                  if (data.checked) {
                    Specialties?.forEach((s) => {
                      if (!SelectedSpecialties.some((sel) => sel.specialtyId === s.id)) {
                        OnSelectSpecialty(s);
                      }
                    });
                  } else {
                    Specialties?.forEach((s) => {
                      const selected = SelectedSpecialties.find(
                        (sel) => sel.specialtyId === s.id
                      );
                      if (selected) OnUnselectSpecialty(selected);
                    });
                  }
                }}
              />
              <div className="SelectionList-item">
                <Label weight="semibold">
                  {t('specialtyMultiSelector.selectAll')}
                </Label>
              </div>
            </div>
          </div>
          {Specialties &&
            Specialties.map((specialty: SpecialtyViewModel) => (
              <div key={specialty.id} className="SelectionListRow">
                <div className="SelectionList">
                  <Switch
                    checked={
                      SelectedSpecialties.find(
                        (s: SelectedSpecialtyType) => s.specialtyId === specialty.id
                      )
                        ? true
                        : false
                    }
                    onChange={(_, data) =>
                      data.checked
                        ? OnSelectSpecialty(specialty)
                        : OnUnselectSpecialty(
                            SelectedSpecialties.find(
                              (s: SelectedSpecialtyType) => s.specialtyId === specialty.id
                            )
                          )
                    }
                  />
                  <div className="SelectionList-item">
                    <Label weight="semibold">
                      {specialty.name}
                    </Label>
                    {specialty.snomedCode && (
                      <Text>{specialty.snomedCode}</Text>
                    )}
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );

  return (
    <GuideDrawer
      open={isOpen}
      size="large"
      title={t('specialtyMultiSelector.headerTitle')}
      onDismiss={HandleCancelForm}
      testId="specialty-multi-selector-drawer"
      secondaryAction={{ label: t('specialtyMultiSelector.closeButton'), testId: 'specialty-multi-selector-drawer-close' }}
    >
      {failOnLoading && (
        <GuideMessageBar intent="error">
          <MessageBarBody>{error?.ErrorMessage}</MessageBarBody>
        </GuideMessageBar>
      )}

      {isLoading ? (
        <Spinner label={t('specialtyMultiSelector.loadingLabel')} />
      ) : (
        optionList
      )}
    </GuideDrawer>
  );
};

const mapStateToProps = (state: AppState, ownProps: IProps) => ({
  isLoading: state.Specialties?.isLoadingAll,
  loadingSuccess: state.Specialties?.successLoadingAll,
  failOnLoading: state.Specialties?.failOnLoadingAll,
  error: state.Specialties?.error,
  Specialties: state.Specialties?.list
});

const mapDispatchToProps = {
  ...SpecialtiesStore.actionCreators
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SpecialtyMultiSelectorComponent as any);
